import React, { useState, useRef, useEffect, useMemo } from "react";
import { Card } from "reactstrap";
import { Search as SearchIcon } from "lucide-react";
import SearchFilterButtons from "../SearchFilterButtons";
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import CommonTable from "../CommonTable";
import AddUnit from "./AddUnit";
import { useLoader } from "../../_helper/LoaderContext";
import { addGlobalUnit, deleteUnit, getGlobalUnitList, getUnitList, statusUnit } from "./service/unit-service";
import { toast } from "react-toastify";
import Select, { components } from 'react-select';

// Custom Checkbox Option
const Option = (props) => {
    return (
        <components.Option {...props}>
            <div className="d-flex align-items-center py-1">
                <input
                    type="checkbox"
                    checked={props.isSelected}
                    onChange={() => null}
                    className="me-2"
                    style={{ pointerEvents: 'none' }}
                />
                <label className="m-0 cursor-pointer w-100">{props.label}</label>
            </div>
        </components.Option>
    );
};
// Select All Option
const allOption = { label: "Select All", value: "*" };

const customStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: '44px',
        height: '44px',
        overflow: 'hidden',
    }),
    valueContainer: (provided) => ({
        ...provided,
        height: '44px',
        padding: '0 8px',
        overflowX: 'auto',        // This enables horizontal scroll
        overflowY: 'hidden',
        flexWrap: 'nowrap',       // Important: prevents wrapping to new line
        whiteSpace: 'nowrap',
        scrollbarWidth: 'thin',   // Firefox
        '&::-webkit-scrollbar': {
            height: '6px',
        },
        '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '10px',
        },
    }),
    multiValue: (styles) => ({
        ...styles,
        backgroundColor: '#1b255b',
        margin: '2px',
        flexShrink: 0,  // Prevents shrinking
    }),
    multiValueLabel: (styles) => ({
        ...styles,
        color: '#fff',
        fontWeight: 500,
    }),
    multiValueRemove: (styles) => ({
        ...styles,
        color: '#d32f2f',
        ':hover': {
            backgroundColor: '#ffcdd2',
            color: 'white',
        },
    }),
    placeholder: (provided) => ({
        ...provided,
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
    }),
};

export default function UnitHome() {
    const { t } = useTranslation();
    const [show, setShow] = useState(false);
    const [edit, setEdit] = useState(false);
    const [data, setData] = useState({});
    const [search, setSearch] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [totalEntries, setTotalEntries] = useState(0);
    const { setLoading } = useLoader();

    // Main unit list (your organization's units)
    const [unit, setUnit] = useState([]);

    // Global units from master list
    const [unitMaster, setUnitMaster] = useState([]);

    // Selected global units (for filtering or submission)
    const [selectedGlobalUnits, setSelectedGlobalUnits] = useState([]);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    const editClose = () => setEdit(false);

    const allOption = useMemo(() => ({ label: t("Select All"), value: "*" }), [t]);

    // Transform unitMaster into react-select format
    const unitOptions = useMemo(() => {
        const options = unitMaster.map(u => ({
            label: u.name,
            value: u._id,
            description: u.description || ""
        }));
        return unitMaster.length > 0 ? [allOption, ...options] : [];
    }, [unitMaster, allOption]);

    // Handle Select Change with "Select All" logic
    const handleGlobalUnitChange = (selected) => {
        // "selected" comes from react-select (may include "Select All")
        if (!selected) {
            setSelectedGlobalUnits([]);
            return;
        }

        const hasSelectAll = selected.some(opt => opt.value === "*");

        if (hasSelectAll) {
            // Toggle Select All
            if (selectedGlobalUnits.length === unitMaster.length) {
                setSelectedGlobalUnits([]); // Deselect all
            } else {
                // Select all real units
                setSelectedGlobalUnits(unitMaster.map(u => ({
                    label: u.name,
                    value: u._id
                })));
            }
        } else {
            // Normal multi-select behavior
            // Filter out the "Select All" option from value
            const realSelections = selected.filter(opt => opt.value !== "*");
            setSelectedGlobalUnits(realSelections);
        }
    };

    // Determine if "Select All" should appear checked
    const selectValue = selectedGlobalUnits;
    // Fetch your unit list. `query` overrides the last submitted search.
    const getList = async (query = appliedSearch) => {
        setLoading(true);
        try {
            const form_data = new FormData();
            form_data.append("search", query);
            form_data.append("page", currentPage);
            form_data.append("limit", entriesPerPage);

            const res = await getUnitList(form_data);
            if (res.data.success) {
                setUnit(res.data.data.data || []);
                setTotalEntries(res.data.data.totalCount || 0);
            }
        } catch (error) {
            console.error("Error fetching units:", error);
            toast.warning(t("Failed to load units"));
        } finally {
            setLoading(false);
        }
    };

    // Fetch global units
    const getGlobalUnit = async () => {
        setLoading(true);
        try {
            const res = await getGlobalUnitList();
            if (res.data.success) {
                setUnitMaster(res.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching global units:", error);
        } finally {
            setLoading(false);
        }
    };

    // Load data
    useEffect(() => {
        getList();
    }, [currentPage, entriesPerPage]);

    const handleSearch = () => {
        const query = search.trim();
        setAppliedSearch(query);
        if (currentPage === 1) {
            getList(query);
        } else {
            setCurrentPage(1);
        }
    };

    const handleClear = () => {
        setSearch("");
        if (!appliedSearch) return;
        setAppliedSearch("");
        if (currentPage === 1) {
            getList("");
        } else {
            setCurrentPage(1);
        }
    };

    // Load global units once
    useEffect(() => {
        getGlobalUnit();
    }, []);

    const totalPages = Math.ceil(totalEntries / entriesPerPage);

    const columns = [
        { header: t("Unit Name"), accessor: "name" },
        { header: t("Description"), accessor: "description" },
    ];

    const handleToggleStatus = (row) => {
        Swal.fire({
            title: t("Are you sure?"),
            text: row.isActive ? t("Deactivate this unit?") : t("Activate this unit?"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: row.isActive ? t("Deactivate") : t("Activate"),
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true);
                try {
                    const res = await statusUnit(row._id);
                    if (res.data.success) {
                        toast.success(res.data.message || t("Status updated"));
                        getList();
                    }
                } catch (error) {
                    toast.warning(t("Failed to update status"));
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleEdit = (row) => {
        setData(row);
        setEdit(true);
    };

    const handleDelete = (row) => {
        Swal.fire({
            title: t("Are you sure?"),
            text: t("You want to delete this unit?"),
            icon: "warning",
            showCancelButton: true,
            cancelButtonColor: "#d33",
            confirmButtonText: t("Yes, delete it!"),
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true);
                try {
                    const res = await deleteUnit(row._id);
                    if (res.data.success) {
                        toast.success(res.data.message || t("Unit deleted"));
                        getList();
                    }
                } catch (error) {
                    toast.warning(t("Failed to delete unit"));
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const actions = [
        { label: t("Edit"), onClick: handleEdit },
        { label: t("Delete"), onClick: handleDelete },
    ];

    // Submit selected global units
    const handleSubmitSelected = async () => {
        const selectedIds = selectedGlobalUnits
            .filter(item => item.value !== "*")
            .map(item => item.value);

        if (selectedIds.length === 0) {
            toast.info(t("No units selected"));
            return;
        }
        const payload = {
            masterUnitIds: selectedIds
        }
        setLoading(true)
        try {
            const res = await addGlobalUnit(payload);
            if (res.data.success == true) {
                toast.success(res.data.message);
                getList();
                setSelectedGlobalUnits([])
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false)
        }
    };

    return (
        <>
            <Card className="shadow-sm">
                <div className="px-3 mt-4">
                    <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                        {/* Search */}
                        <div className="input-group" style={{ flex: "1 1 240px", maxWidth: 420 }}>
                            <span className="input-group-text bg-white border-end-0">
                                <SearchIcon size={20} />
                            </span>
                            <input
                                type="text"
                                className="form-control border-start-0"
                                placeholder={t("Search by unit name")}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSearch();
                                }}
                            />
                        </div>
                        <SearchFilterButtons onSearch={handleSearch} onClear={handleClear} />

                        <div className="ms-auto">
                            <button className="btn btn-primary" onClick={handleShow}>
                                {t("Add")}
                            </button>
                        </div>
                    </div>

                    {/* Table */}
                    <div className="row">
                        <div className="col-12">
                            <CommonTable
                                columns={columns}
                                data={unit}
                                onToggleStatus={handleToggleStatus}
                                onAction={actions}
                                totalPages={totalPages}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                entriesPerPage={entriesPerPage}   // pass value
                                setEntriesPerPage={setEntriesPerPage}  // pass setter
                            />
                        </div>
                    </div>
                </div>
            </Card>

            {/* Add Modal */}
            <Modal show={show} onHide={handleClose} centered>
                <AddUnit handleClose={handleClose} getList={getList} />
            </Modal>

            {/* Edit Modal */}
            <Modal show={edit} onHide={editClose} centered>
                <AddUnit handleClose={editClose} data={data} getList={getList} />
            </Modal>
        </>
    );
}